import { useState } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import { ModalForm, Notice } from "@ficatranquilo/contabilidade.com-ui";
import useService from "@hooks/useService";
import { ClockAdjustmentFields, validateClockAdjustment } from "@components/ClockAdjustments";
import { isoToDate } from "@helpers/localDate";
import { clockDateTime } from "@helpers/timeClock";
import { focusFirstError } from "../../Common/FormPage";

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const IDS = { date: "request-adjustment-date", time: "request-adjustment-time", reason: "request-adjustment-reason" };
const FIELDS = [IDS.date, IDS.time, IDS.reason];

/**
 * Pedido de inclusão de uma batida esquecida, que o gestor de ponto ou o
 * administrador aprova. A batida não pode ser anterior ao início do contrato nem
 * estar no futuro (o back confere os dois).
 *
 * Props: initialDate ('YYYY-MM-DD', vinda do dia do espelho), startDate (início do
 * contrato, 'YYYY-MM-DD'), onClose, onSaved.
 */
export default function RequestAdjustmentModal({ initialDate, startDate, onClose, onSaved }) {
  const timeClockService = useService("timeClock");
  const [values, setValues] = useState({ date: isoToDate(initialDate), time: "", reason: "" });
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);

  const errors = submitted ? validateClockAdjustment(values, IDS) : {};
  const set = (field, value) => setValues((prev) => ({ ...prev, [field]: value }));

  function close() {
    if (!saving) onClose();
  }

  async function save() {
    setSubmitted(true);
    const found = validateClockAdjustment(values, IDS);
    if (Object.keys(found).length) {
      focusFirstError(FIELDS.filter((id) => found[id]));
      return;
    }

    setSaving(true);
    const result = await timeClockService.createAdjustment({
      requested_clock_time: clockDateTime(values.date, values.time).toISOString(),
      reason: values.reason.trim(),
    });
    setSaving(false);
    // Em erro, o sendError do service já mostrou o toast; o modal continua aberto
    if (!result || result.error) return;
    toast.success("Solicitação de ajuste enviada!");
    onSaved();
  }

  return (
    <ModalForm
      $showModal
      $setShowModal={close}
      $noClose={saving}
      $isLoading={saving}
      $size="medium"
      $title="Solicitar ajuste de ponto"
      $contentBackground="N0"
      $actions={[
        { text: "Cancelar", action: close, variant: "outline", isDisabled: saving },
        { text: "Enviar solicitação", action: save, variant: "primary", isDisabled: saving },
      ]}>
      <Stack>
        <Notice
          $variant="gray"
          $text="A batida entra no seu espelho só depois de aprovada pelo gestor de ponto ou pelo administrador. As batidas originais não mudam."
        />
        <ClockAdjustmentFields
          ids={IDS}
          values={values}
          errors={errors}
          onChange={set}
          minDate={isoToDate(startDate) || undefined}
          reasonPlaceholder="Ex.: esqueci de registrar a saída para o almoço"
        />
      </Stack>
    </ModalForm>
  );
}
